import {
  type CoreError,
  getCurrentUser as coreGetCurrentUser,
  getMembership as coreGetMembership,
  getTenantBySlug as coreGetTenantBySlug,
  isTenantMember as coreIsTenantMember,
  updateCurrentUser as coreUpdateCurrentUser,
  isCoreError,
} from '@sazeo/core';
import type { LangCode, UpdateUserInput } from '@sazeo/schemas';
import { redirect } from 'react-router-dom';
import { getWorkspaceChooserUrl } from './tenantHost';

/**
 * Loader-facing tenant/user types.
 *
 * We keep these decoupled from the raw `@sazeo/core` return shapes so that:
 *   - `display_name` is always a non-null string for UI consumers (AppShell,
 *     HomePage, tests). Core returns `string | null`; we fall back to the
 *     email local-part.
 *   - Downstream components (AppShell, HomePage, ProfileSettings) keep the
 *     exact same contract they had under the Wave-2 placeholders.
 */
export interface TenantRecord {
  id: string;
  slug: string;
  name: string;
  logo_url: string | null;
}

export interface UserProfile {
  id: string;
  email: string | null;
  display_name: string;
  avatar_url: string | null;
  preferred_lang: LangCode;
}

export interface MembershipSummary {
  tenant_id: string;
  role_name: string | null;
  role_id: string | null;
  joined_at: string;
}

/**
 * CoreError → loader Response mapping convention.
 *
 * Loader callers cannot receive a plain object that looks like data — React
 * Router would render a broken UI. We translate every `CoreError` into a
 * `throw`:
 *   - NOT_FOUND            → 404 Response (triggers `errorElement`)
 *   - FORBIDDEN / VALIDATION → redirect to the workspace chooser (user is
 *     signed in but either not authorized or sent bad input — in both cases
 *     the safe next step is "pick a workspace")
 *   - INTERNAL / CONFLICT  → 500 Response with the error message for debug
 *
 * The `isDev` flag lets us build an absolute URL on production domains
 * (`app.sazeo.io`) vs localhost, matching the existing tenantHost helpers.
 */
function throwCoreError(err: CoreError, isDev: boolean): never {
  switch (err.code) {
    case 'NOT_FOUND':
      throw new Response(err.message, { status: 404, statusText: 'Not Found' });
    case 'FORBIDDEN':
    case 'VALIDATION':
      throw redirect(getWorkspaceChooserUrl(isDev));
    case 'CONFLICT':
      throw new Response(err.message, { status: 409, statusText: 'Conflict' });
    default:
      throw new Response(err.message, {
        status: 500,
        statusText: 'Internal Error',
      });
  }
}

/** Coerce `@sazeo/core`'s `Tenant` row into the loader-facing `TenantRecord`. */
function toTenantRecord(t: {
  id: string;
  slug: string;
  name: string;
  logo_url: string | null;
}): TenantRecord {
  return {
    id: t.id,
    slug: t.slug,
    name: t.name,
    logo_url: t.logo_url,
  };
}

/**
 * Coerce `@sazeo/core`'s `User` row into the loader-facing `UserProfile`.
 * `display_name` may be null at the DB layer (signup hook may not have run);
 * fall back to the email local-part so the UI always has something to show.
 */
function toUserProfile(u: {
  id: string;
  email: string | null;
  display_name: string | null;
  avatar_url: string | null;
  preferred_lang: LangCode;
}): UserProfile {
  return {
    id: u.id,
    email: u.email,
    display_name:
      u.display_name && u.display_name.trim().length > 0
        ? u.display_name
        : (u.email?.split('@')[0] ?? 'Member'),
    avatar_url: u.avatar_url,
    preferred_lang: u.preferred_lang,
  };
}

export async function getTenantBySlug(
  slug: string,
  opts: { isDev?: boolean } = {},
): Promise<TenantRecord | null> {
  const result = await coreGetTenantBySlug(slug);
  if (isCoreError(result)) {
    // NOT_FOUND is the normal "wrong subdomain" case — let the caller
    // decide (it redirects to the workspace chooser). Anything else is
    // a genuine failure that should surface.
    if (result.code === 'NOT_FOUND') return null;
    throwCoreError(result, opts.isDev ?? false);
  }
  return toTenantRecord(result);
}

export async function getCurrentUserProfile(
  userId: string,
  email: string | null,
  opts: { isDev?: boolean } = {},
): Promise<UserProfile> {
  const result = await coreGetCurrentUser();
  if (isCoreError(result)) {
    // Fallback: if the row is missing (signup hook drift) synthesise a
    // minimal profile so the app doesn't hard-crash. Real drift surfaces
    // as a tracked issue rather than a blocked UI.
    if (result.code === 'NOT_FOUND') {
      return {
        id: userId,
        email,
        display_name: email?.split('@')[0] ?? 'Member',
        avatar_url: null,
        preferred_lang: 'ka' as LangCode,
      };
    }
    throwCoreError(result, opts.isDev ?? false);
  }
  return toUserProfile(result);
}

export async function isTenantMember(
  userId: string,
  tenantId: string,
  opts: { isDev?: boolean } = {},
): Promise<boolean> {
  const result = await coreIsTenantMember(userId, tenantId);
  if (isCoreError(result)) {
    throwCoreError(result, opts.isDev ?? false);
  }
  return result;
}

export async function getCurrentMembership(
  userId: string,
  tenantId: string,
  opts: { isDev?: boolean } = {},
): Promise<MembershipSummary | null> {
  const result = await coreGetMembership(userId, tenantId);
  if (isCoreError(result)) {
    if (result.code === 'NOT_FOUND') return null;
    throwCoreError(result, opts.isDev ?? false);
  }

  return {
    tenant_id: result.tenant_id,
    role_name: result.role?.name ?? null,
    role_id: result.role_id ?? null,
    joined_at: result.joined_at,
  };
}

export async function updateUserProfile(patch: UpdateUserInput): Promise<UserProfile> {
  const result = await coreUpdateCurrentUser(patch);
  if (isCoreError(result)) {
    // For profile writes we propagate the CoreError to the caller so the
    // form can render an inline error (no page-level redirect). Callers
    // use `isCoreError` to check before consuming the return.
    throw result;
  }
  return toUserProfile(result);
}
